function callAnalytics(method, ...args) {
    try {
        if (window.analytics && typeof window.analytics[method] === 'function') {
            window.analytics[method](...args);
            return true;
        }
        window.siteIntegrationsQueue = window.siteIntegrationsQueue || [];
        if (window.siteIntegrationsQueue.length < 200) {
            window.siteIntegrationsQueue.push([method, args]);
            return true;
        }
    } catch {
        // Tracking must never interfere with storefront behavior.
    }
    return false;
}

export function trackStorefrontInteraction(name, details = {}) {
    if (
        ['recommendation_interaction', 'recommendation_request', 'recommendation_render'].includes(
            name,
        )
    ) {
        return withRecommendationTracking(() => callAnalytics('trackInteraction', name, details));
    }
    return callAnalytics('trackInteraction', name, details);
}

function withRecommendationTracking(action) {
    const deliver = () => {
        const config = window.siteIntegrationsConfig;
        if (
            config?.serverTracking?.recommendationEnabled === false ||
            (config?.privateInitialization && config.serverTracking?.recommendationEnabled !== true)
        )
            return false;
        return action();
    };
    if (window.shopInitialization) {
        void Promise.resolve(window.shopInitialization).then(deliver, deliver);
        return true;
    }
    return deliver();
}

function productFromCard(card) {
    const product = {
        id: card.dataset.productId,
        name: card.dataset.productName || '',
        brand: card.dataset.productBrand || '',
        variant: card.dataset.productVariant || '',
    };
    if (card.dataset.productPrice !== undefined && card.dataset.productPrice !== '') {
        product.price = Number(card.dataset.productPrice) || 0;
    }
    return product;
}

function productsFromList(root, excludeNestedComponents = false) {
    return [...root.querySelectorAll('[data-product-id][data-product-name]')]
        .filter((card) => !excludeNestedComponents || !card.closest('[data-analytics-component]'))
        .map(productFromCard);
}

function trackingRoot(scope) {
    if (scope instanceof Element && scope.matches('[data-z-tracking-view]')) return scope;
    return scope.querySelector?.('[data-z-tracking-view]') || null;
}

function initializePageView(scope = document) {
    const root = trackingRoot(scope);
    if (!root) return;
    const products = productsFromList(root, true);
    if (root.dataset.zTrackingView === 'product') {
        callAnalytics('viewProduct', {
            id: root.dataset.productId,
            name: root.dataset.productName || '',
            category: root.dataset.productCategory || '',
            brand: root.dataset.productBrand || '',
            variant: root.dataset.productVariant || '',
            price: Number(root.dataset.productPrice) || 0,
        });
    } else {
        if (root.dataset.zTrackingView === 'search') {
            callAnalytics(
                'viewSearch',
                root.dataset.zTrackingQuery || '',
                Number(root.dataset.zTrackingCount) || 0,
            );
        }
        callAnalytics(
            'viewCategory',
            {
                id: root.dataset.zTrackingId || root.dataset.zTrackingView,
                name: root.dataset.zTrackingName || '',
            },
            products,
            root.dataset.zTrackingView === 'category',
            root,
        );
    }
}

function componentDetails(component) {
    const productIds = new Set(
        [...component.querySelectorAll('[data-product-id]')]
            .map((item) => item.dataset.productId)
            .filter(Boolean),
    );
    return {
        source_context: 'storefront_module',
        action: 'view',
        component_type:
            component.dataset.analyticsComponentType ||
            component.dataset.analyticsComponent ||
            'module',
        component_id: component.dataset.analyticsComponentId || 'unknown',
        position: Number(component.dataset.analyticsPosition) || 0,
        item_count: productIds.size,
    };
}

const visibleRecommendationCards = new WeakSet();
const visibleRecommendationModules = new WeakSet();

export function trackVisibleProductRecommendation(card) {
    withRecommendationTracking(() => recordVisibleProductRecommendation(card));
}

function recordVisibleProductRecommendation(card) {
    if (window.siteIntegrationsConfig?.serverTracking?.recommendationEnabled === false) return;
    if (visibleRecommendationCards.has(card)) return;
    const component = card.closest(
        '[data-recommendation-placement], [data-product-page-recommendations], [data-cart-add-recommendations], [data-cart-page-recommendations]',
    );
    if (!component) return;
    visibleRecommendationCards.add(card);

    // Recommendation lists are measured only after an actual card
    // has remained visible, never when the (possibly offscreen) module renders.
    callAnalytics(
        'viewCategory',
        {
            id: component.dataset.analyticsComponentId || 'product.recommended',
            name: component.dataset.analyticsComponentType || 'Kunden kauften auch',
        },
        [productFromCard(card)],
        false,
        component,
    );
    if (visibleRecommendationModules.has(component)) return;
    visibleRecommendationModules.add(component);
    callAnalytics('trackInteraction', 'feature_impression', componentDetails(component));
}

const activeModuleObservers = new Set();
let observedModuleComponents = new WeakSet();
let visibleModuleComponents = new WeakSet();

function stopModuleImpressions() {
    activeModuleObservers.forEach((stop) => stop());
    activeModuleObservers.clear();
    observedModuleComponents = new WeakSet();
    visibleModuleComponents = new WeakSet();
}

function initializeModuleImpressions(scope = document) {
    if (!document.body?.classList.contains('zirbelino-page--migrated')) return;
    const components = [
        ...(scope instanceof Element && scope.matches('[data-analytics-component]') ? [scope] : []),
        ...scope.querySelectorAll('[data-analytics-component]'),
    ].filter(
        (component) =>
            !component.matches(
                '[data-recommendation-placement], [data-product-page-recommendations], [data-cart-add-recommendations], [data-cart-page-recommendations]',
            ),
    );
    if (!components.length) return;
    components.forEach((component) => {
        const products = productsFromList(component);
        if (!products.length) return;
        callAnalytics(
            'viewCategory',
            {
                id: component.dataset.analyticsComponentId || 'storefront-module',
                name:
                    component.dataset.analyticsComponentType ||
                    component.dataset.analyticsComponent ||
                    'Modul',
            },
            products,
            false,
            component,
        );
    });
    if (!('IntersectionObserver' in window)) return;
    const newComponents = components.filter((component) => {
        if (observedModuleComponents.has(component) || visibleModuleComponents.has(component))
            return false;
        observedModuleComponents.add(component);
        return true;
    });
    if (!newComponents.length) return;
    const timers = new Map();
    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                const sufficientlyVisible =
                    entry.intersectionRatio >= 0.25 ||
                    entry.intersectionRect.height >= Math.min(250, window.innerHeight * 0.25);
                if (entry.isIntersecting && sufficientlyVisible) {
                    if (timers.has(entry.target)) return;
                    timers.set(
                        entry.target,
                        window.setTimeout(() => {
                            timers.delete(entry.target);
                            if (visibleModuleComponents.has(entry.target)) return;
                            visibleModuleComponents.add(entry.target);
                            observer.unobserve(entry.target);
                            callAnalytics(
                                'trackInteraction',
                                'feature_impression',
                                componentDetails(entry.target),
                            );
                        }, 500),
                    );
                } else if (timers.has(entry.target)) {
                    window.clearTimeout(timers.get(entry.target));
                    timers.delete(entry.target);
                }
            });
        },
        { threshold: [0, 0.25] },
    );
    activeModuleObservers.add(() => {
        observer.disconnect();
        timers.forEach((timer) => window.clearTimeout(timer));
        timers.clear();
    });
    newComponents.forEach((component) => observer.observe(component));
}

function initializeCatalogInteractions() {
    document.addEventListener('click', (event) => {
        const link = event.target.closest('.z-product-card__link, .z-product-card__media');
        const card = link?.closest('[data-product-id][data-product-name]');
        if (!card) return;
        const page = card.closest('[data-z-tracking-view]');
        const recommendation = card.closest(
            '[data-recommendation-placement], [data-product-page-recommendations], [data-cart-add-recommendations], [data-cart-page-recommendations]',
        );
        const placement =
            recommendation?.dataset.recommendationPlacement ||
            (recommendation?.hasAttribute('data-cart-add-recommendations')
                ? 'cart_add'
                : recommendation?.hasAttribute('data-cart-page-recommendations')
                  ? 'cart_page'
                  : 'product_page');
        const select = () =>
            callAnalytics(
                'selectItem',
                productFromCard(card),
                {
                    id:
                        recommendation?.dataset.analyticsComponentId ||
                        page?.dataset.zTrackingId ||
                        'product-list',
                    name: recommendation
                        ? 'Kunden kauften auch'
                        : page?.dataset.zTrackingName || 'Produktliste',
                    source_context: recommendation ? `${placement}_recommendation` : 'product_list',
                },
                [...card.parentElement.children].indexOf(card) + 1,
            );
        if (recommendation) withRecommendationTracking(select);
        else select();
    });
}

function initializeCartTracking() {
    const synchronize = (detail) => {
        const items = Array.isArray(detail.ecommerce_items) ? detail.ecommerce_items : [];
        const total = Number(detail.analytics_cart_value) || 0;
        return callAnalytics('cartSync', total, items);
    };
    document.addEventListener('zirbelino:cart:add', (event) => {
        const detail = event.detail || {};
        const total = Number(detail.analytics_cart_value) || 0;
        const matomoCartSynced = synchronize(detail);
        if (!detail.product) return;
        callAnalytics(
            'cartAdd',
            total,
            {
                id: detail.product.product_id,
                name: detail.product.name,
                category: detail.product.category || '',
                brand: detail.product.brand || '',
                variant: detail.product.variant || '',
                price: Number(detail.product.price) || 0,
                quantity: Number(detail.product.quantity) || 1,
                source_context: detail.zirbelino_source_context || 'product_list',
            },
            false,
            Boolean(matomoCartSynced),
            detail.tracking_event_id || null,
        );
    });
    document.addEventListener('zirbelino:cart:change', (event) => {
        synchronize(event.detail || {});
    });
}

let passiveTrackingStarted = false;

function initializePassiveTracking() {
    if (passiveTrackingStarted) return;
    passiveTrackingStarted = true;
    initializePageView();
    initializeModuleImpressions();
}

function schedulePassiveTracking() {
    const scheduleWhenIdle = () => {
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(initializePassiveTracking, { timeout: 1_500 });
            return;
        }
        window.setTimeout(initializePassiveTracking, 250);
    };

    if (document.readyState === 'complete') scheduleWhenIdle();
    else window.addEventListener('load', scheduleWhenIdle, { once: true });
}

export function initStorefrontTracking() {
    initializeCatalogInteractions();
    initializeCartTracking();
    schedulePassiveTracking();
    window.addEventListener('pagehide', stopModuleImpressions);
    window.addEventListener('shop:restored', () => {
        stopModuleImpressions();
        if (passiveTrackingStarted) {
            initializePageView();
            initializeModuleImpressions();
        }
    });
    document.addEventListener('zirbelino:content-updated', (event) => {
        const root = event.detail?.root;
        if (!(root instanceof Element) || !passiveTrackingStarted) return;
        initializePageView(root);
        initializeModuleImpressions(root);
    });
}
